/* Igniton — The Great Ignition (Black Friday 2026)
   1. Applies the discount code to the cart (Cart API) on load and after
      every add to cart, so the cart, drawer and checkout all show it.
   2. Switches Early Access -> Final Days at data-final-at.
   3. Runs the countdown to whichever phase is live.
   4. Runs a spark along the fuse, igniting each step's orb as it arrives. */
(function () {
  'use strict';

  function pad(n) { return String(n).padStart(2, '0'); }
  function parse(s) { var t = Date.parse(s || ''); return isNaN(t) ? null : t; }

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function postJSON(path, body) {
    return fetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }

  // Put the page's code on the cart itself (Cart API "discount" field, the
  // same way ig-dm-cart-sync applies the mailer codes), so the cart, drawer
  // and checkout all show it. The /discount/CODE visit is kept as a backup:
  // it sets the checkout cookie for shoppers whose cart is created elsewhere.
  function applyCode(root) {
    var code = root.getAttribute('data-code');
    if (!code || root.getAttribute('data-auto-apply') !== 'true') return Promise.resolve();
    fetch(ROOT + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin', redirect: 'manual' })
      .catch(function () {});
    return postJSON('cart/update.js', { discount: code })
      .then(function (cart) {
        var on = (cart.discount_codes || []).some(function (d) {
          return String(d.code).toUpperCase() === code.toUpperCase() && d.applicable !== false;
        });
        root.setAttribute('data-code-status', on ? 'applied' : 'pending');
        return cart;
      })
      .catch(function (e) {
        root.setAttribute('data-code-status', 'error');
        if (window.console) console.warn('[ig-gi] could not apply ' + code, e);
      });
  }

  function initPhase(root) {
    var finalAt = parse(root.getAttribute('data-final-at'));
    var endsAt = parse(root.getAttribute('data-ends-at'));
    var box = root.querySelector('[data-ig-countdown]');
    var d = root.querySelector('[data-ig-days]');
    var h = root.querySelector('[data-ig-hours]');
    var m = root.querySelector('[data-ig-mins]');

    function tick() {
      var now = Date.now();
      var isFinal = finalAt !== null && now >= finalAt;
      root.classList.toggle('is-final', isFinal);
      var target = isFinal ? endsAt : (finalAt || endsAt);
      if (!box || target === null || now >= target) {
        if (box) box.hidden = true;
        return;
      }
      var left = Math.floor((target - now) / 60000);
      d.textContent = pad(Math.floor(left / 1440));
      h.textContent = pad(Math.floor((left % 1440) / 60));
      m.textContent = pad(left % 60);
      box.hidden = false;
      box.setAttribute('aria-label',
        (isFinal ? 'Sale ends in ' : 'Early access ends in ') +
        Math.floor(left / 1440) + ' days, ' + Math.floor((left % 1440) / 60) + ' hours, ' + (left % 60) + ' minutes');
    }
    tick();
    setInterval(tick, 30000);
  }

  function initFuse(root) {
    var fuse = root.querySelector('[data-ig-fuse]');
    if (!fuse) return;
    var track = fuse.querySelector('[data-ig-track]');
    var steps = Array.prototype.slice.call(fuse.querySelectorAll('[data-ig-step]'));
    var nodes = steps.map(function (s) { return s.querySelector('[data-ig-node]'); });
    if (!steps.length || !track || nodes.some(function (n) { return !n; })) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var last = steps.length - 1;

    // Lay the track from the centre of the first orb to the centre of the last,
    // across on desktop and down the side on phones.
    function measure() {
      var f = fuse.getBoundingClientRect();
      var a = nodes[0].getBoundingClientRect();
      var z = nodes[last].getBoundingClientRect();
      var ax = a.left + a.width / 2 - f.left, ay = a.top + a.height / 2 - f.top;
      var zx = z.left + z.width / 2 - f.left, zy = z.top + z.height / 2 - f.top;
      var vertical = Math.abs(zx - ax) < 4 && last > 0;
      fuse.classList.toggle('is-vertical', vertical);
      if (vertical) {
        track.style.cssText = 'left:' + (ax - 2) + 'px;top:' + ay + 'px;width:4px;height:' + Math.max(0, zy - ay) + 'px;';
      } else {
        track.style.cssText = 'left:' + ax + 'px;top:' + (ay - 2) + 'px;height:4px;width:' + Math.max(0, zx - ax) + 'px;';
      }
      fuse.classList.add('is-measured');
    }
    measure();
    var rt;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(measure, 120); });

    function setBurn(i) {
      fuse.style.setProperty('--ig-gi-fuse', (last === 0 ? 100 : Math.round((i / last) * 100)) + '%');
    }

    function ignite(all) {
      if (all) {
        steps.forEach(function (s) { s.classList.add('is-lit'); });
        setBurn(last);
        fuse.classList.add('is-done');
        return;
      }
      var hop = 1300; // ms between orbs (1s of burn + a beat to ignite)
      fuse.classList.add('is-burning');
      setTimeout(function () { steps[0].classList.add('is-lit'); }, 150);
      for (var i = 1; i <= last; i++) {
        (function (i) {
          setTimeout(function () { setBurn(i); }, 300 + (i - 1) * hop);
          setTimeout(function () { steps[i].classList.add('is-lit'); }, 300 + (i - 1) * hop + 1000);
        })(i);
      }
      setTimeout(function () {
        fuse.classList.remove('is-burning');
        fuse.classList.add('is-done');
      }, 300 + last * hop + 600);
    }

    if (reduce || !('IntersectionObserver' in window)) { ignite(true); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { io.disconnect(); measure(); ignite(false); }
      });
    }, { threshold: 0.4 });
    io.observe(fuse);
  }

  // Add to cart with the Cart API, apply the code to that cart, then go to
  // the cart page. If anything fails, fall back to the plain form post.
  function initForms(root) {
    root.querySelectorAll('[data-ig-atc]').forEach(function (form) {
      form.addEventListener('submit', function (ev) {
        if (form.__igPlain) return;
        ev.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
        var id = form.querySelector('input[name="id"]');
        var qty = form.querySelector('input[name="quantity"]');
        var back = form.querySelector('input[name="return_to"]');
        postJSON('cart/add.js', { items: [{ id: Number(id && id.value), quantity: Number((qty && qty.value) || 1) }] })
          .then(function () { return applyCode(root); })
          .then(function () { window.location.href = (back && back.value) || ROOT + 'cart'; })
          .catch(function () {
            form.__igPlain = true;
            if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
            form.submit();
          });
      });
    });
  }

  function init(root) {
    if (root.__igInit) return;
    root.__igInit = true;
    applyCode(root);
    initPhase(root);
    initFuse(root);
    initForms(root);
  }

  function boot() { document.querySelectorAll('[data-ig-gi]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor: re-init when the section reloads.
  document.addEventListener('shopify:section:load', boot);
})();
